import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Alert, Box, Button, Paper, Stack, TextField, Typography } from '@mui/material'
import { ArrowBack as ArrowBackIcon } from '@mui/icons-material'
import { useCreateCompany } from '../../hooks/api/useCompanies'
import { useToast } from '../../contexts/ToastContext'
import { CreateCompanyPayload } from '../../types/company'
import { useUnsavedChanges } from '../../contexts/UnsavedChangesContext'

const initialForm: CreateCompanyPayload = {
  name: '', email: '', phone: '', address: '', website: '', industry: '',
  initial_admin: { name: '', email: '', password: '' },
}

const CompanyCreatePage = () => {
  const navigate = useNavigate()
  const { showToast } = useToast()
  const createCompany = useCreateCompany()
  const [form, setForm] = useState<CreateCompanyPayload>(initialForm)
  const [dirty, setDirty] = useState(false)
  const [error, setError] = useState('')
  const { setUnsavedChanges, confirmLeave } = useUnsavedChanges()

  useEffect(() => {
    setUnsavedChanges(dirty)
    return () => setUnsavedChanges(false)
  }, [dirty, setUnsavedChanges])

  const setField = (key: keyof Omit<CreateCompanyPayload, 'initial_admin'>, value: string) => {
    setForm((current) => ({ ...current, [key]: value }))
    setDirty(true)
  }
  const setAdminField = (key: keyof CreateCompanyPayload['initial_admin'], value: string) => {
    setForm((current) => ({ ...current, initial_admin: { ...current.initial_admin, [key]: value } }))
    setDirty(true)
  }
  const goBack = () => {
    if (confirmLeave()) navigate('/companies')
  }
  const submit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError('')
    try {
      const result = await createCompany.mutateAsync(form)
      setDirty(false)
      showToast('Company created successfully', 'success')
      navigate(`/companies/${result.company._id}`, { replace: true })
    } catch (reason) {
      setError((reason as Error).message || 'Could not create company.')
    }
  }

  return (
    <Box sx={{ maxWidth: 850, mx: 'auto' }}>
      <Button startIcon={<ArrowBackIcon />} onClick={goBack} sx={{ mb: 2 }}>Companies</Button>
      <Typography variant="h4" fontWeight={700} gutterBottom>Create company</Typography>
      <Typography color="text.secondary" mb={3}>The company starts active with default features and billing settings.</Typography>
      <Box component="form" onSubmit={submit}>
        <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" mb={2}>Company profile</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField label="Company name" required value={form.name} onChange={(event) => setField('name', event.target.value)} inputProps={{ minLength: 2, maxLength: 100 }} />
            <TextField label="Company email" type="email" required value={form.email} onChange={(event) => setField('email', event.target.value)} />
            <TextField label="Phone" required value={form.phone} onChange={(event) => setField('phone', event.target.value)} inputProps={{ minLength: 7, maxLength: 20 }} />
            <TextField label="Address" required value={form.address} onChange={(event) => setField('address', event.target.value)} />
            <TextField label="Website" value={form.website} onChange={(event) => setField('website', event.target.value)} />
            <TextField label="Industry" value={form.industry} onChange={(event) => setField('industry', event.target.value)} />
          </Box>
        </Paper>
        <Paper variant="outlined" sx={{ p: 3, mb: 2 }}>
          <Typography variant="h6" mb={0.5}>Initial super admin</Typography>
          <Typography color="text.secondary" variant="body2" mb={2}>Create the first user who can sign in to this company.</Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
            <TextField label="Full name" required value={form.initial_admin.name} onChange={(event) => setAdminField('name', event.target.value)} inputProps={{ minLength: 4 }} />
            <TextField label="Email" type="email" required value={form.initial_admin.email} onChange={(event) => setAdminField('email', event.target.value)} />
            <TextField label="Password" type="password" required value={form.initial_admin.password} onChange={(event) => setAdminField('password', event.target.value)} inputProps={{ minLength: 8 }} helperText="At least 8 characters" />
          </Box>
        </Paper>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <Button onClick={goBack} disabled={createCompany.isPending}>Cancel</Button>
          <Button type="submit" variant="contained" disabled={createCompany.isPending}>{createCompany.isPending ? 'Creating...' : 'Create company'}</Button>
        </Stack>
      </Box>
    </Box>
  )
}

export default CompanyCreatePage
